import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ApiServices from '../Services/ApiServices';
import { toastHelper } from '../../Helpers/toastHelper';
import Loader from '../Shared/Loader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faLayerGroup,
    faSave,
    faArrowRight,
    faPuzzlePiece,
    faPlusCircle,
    faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';
import { useFormik } from 'formik';
import * as Yup from 'yup';

const AddPlanFeature = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { planId, planName } = location.state || {};

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [plans, setPlans] = useState([]);
    const [featureCatalog, setFeatureCatalog] = useState([]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [plansRes, featuresRes] = await Promise.all([
                ApiServices.get('Lookup/Plans'),
                ApiServices.get('Subscription/PlanFeture/getAll')
            ]);

            // Handle different API structures (direct array vs. {data: []})
            const plansData = plansRes?.data || plansRes || [];
            const featuresData = featuresRes?.data || featuresRes || [];

            setPlans(Array.isArray(plansData) ? plansData : []);
            setFeatureCatalog(Array.isArray(featuresData) ? featuresData : []);
        } catch (error) {
            toastHelper.error('فشل في تحميل البيانات اللازمة');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const formik = useFormik({
        initialValues: {
            plan_id: planId || '',
            plan_feature_id: '',
            included: true,
            screen_name: ''
        },
        enableReinitialize: true,
        validationSchema: Yup.object({
            plan_id: Yup.number().required('يرجى اختيار الخطة'),
            plan_feature_id: Yup.number().required('يرجى اختيار الميزة'),
            screen_name: Yup.string().required('يرجى إدخال اسم الشاشة البرمجي')
        }),
        onSubmit: async (values) => {
            setSubmitting(true);
            try {
                const response = await ApiServices.post('Subscription/PlanPlanFeture/create', {
                    plan_id: parseInt(values.plan_id),
                    plan_feature_id: parseInt(values.plan_feature_id),
                    included: values.included,
                    screen_name: values.screen_name.trim()
                });
                toastHelper.success('تم ربط الميزة بالخطة بنجاح');
                navigate(-1);
            } catch (error) {
                toastHelper.error(error.message || error.msg || 'فشل في إضافة الميزة للخطة');
            } finally {
                setSubmitting(false);
            }
        }
    });

    const customSelectStyles = {
        container: (base) => ({ ...base, width: '100%' }),
        control: (base, state) => ({
            ...base,
            padding: '6px',
            borderRadius: '12px',
            border: state.isFocused ? '2px solid #6366f1' : '2px solid #e2e8f0',
            fontFamily: 'Zain, sans-serif'
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected ? '#6366f1' : state.isFocused ? '#f1f5f9' : 'white',
            color: state.isSelected ? 'white' : '#334155',
            fontFamily: 'Zain, sans-serif'
        }),
        singleValue: (base) => ({ ...base, fontFamily: 'Zain, sans-serif' })
    };

    if (loading) return <Loader text="جاري التحميل..." />;

    return (
        <div className="assign-feature-page">
            <div className="form-container">
                <header className="form-header">
                    <button className="back-btn" onClick={() => navigate(-1)}>
                        <FontAwesomeIcon icon={faArrowRight} />
                        <span>عودة</span>
                    </button>
                    <h1>تعيين ميزة لخطة</h1>
                </header>

                <form onSubmit={formik.handleSubmit}>
                    <div className="form-grid">
                        <div className="input-group">
                            <label><FontAwesomeIcon icon={faLayerGroup} /> الخطة المستهدفة</label>
                            <Select
                                options={plans.map(p => ({ value: p.id, label: p.name }))}
                                value={plans.map(p => ({ value: p.id, label: p.name })).find(o => o.value === formik.values.plan_id)}
                                isRtl={true}
                                isDisabled={!!planId}
                                styles={customSelectStyles}
                                placeholder="اختر الخطة..."
                                onChange={(opt) => formik.setFieldValue('plan_id', opt ? opt.value : '')}
                            />
                            {formik.touched.plan_id && formik.errors.plan_id && <div className="field-error">{formik.errors.plan_id}</div>}
                        </div>

                        <div className="input-group">
                            <label><FontAwesomeIcon icon={faPuzzlePiece} /> الميزة المتاحة</label>
                            <Select
                                options={featureCatalog.map(f => ({ value: f.id, label: f.name, screen: f.screen_name }))}
                                value={featureCatalog.map(f => ({ value: f.id, label: f.name, screen: f.screen_name })).find(o => o.value === formik.values.plan_feature_id)}
                                isRtl={true}
                                styles={customSelectStyles}
                                placeholder="اختر الميزة من القاموس..."
                                onChange={(opt) => {
                                    formik.setFieldValue('plan_feature_id', opt ? opt.value : '');
                                    // If we are picking a feature, name and screen are fixed from catalog
                                    if (opt && opt.screen) {
                                        formik.setFieldValue('screen_name', opt.screen);
                                    }
                                }}
                            />
                            {formik.touched.plan_feature_id && formik.errors.plan_feature_id && <div className="field-error">{formik.errors.plan_feature_id}</div>}
                        </div>

                        <div className="input-group full-width">
                            <label><FontAwesomeIcon icon={faPlusCircle} /> الاسم البرمجي (Screen Name)</label>
                            <input
                                {...formik.getFieldProps('screen_name')}
                                placeholder="سيتم تعبئته تلقائياً عند اختيار الميزة"
                                className={formik.touched.screen_name && formik.errors.screen_name ? 'input-error' : ''}
                            />
                            {formik.touched.screen_name && formik.errors.screen_name && <div className="field-error">{formik.errors.screen_name}</div>}
                        </div>

                        <div className="input-group full-width">
                            <label className="checkbox-toggle">
                                <div className="label-info">
                                    <FontAwesomeIcon icon={faCheckCircle} style={{ color: formik.values.included ? '#10b981' : '#94a3b8' }} />
                                    <span>هل الميزة متضمنة (تحت التنفيذ)؟</span>
                                </div>
                                <input
                                    type="checkbox"
                                    checked={formik.values.included}
                                    onChange={(e) => formik.setFieldValue('included', e.target.checked)}
                                />
                            </label>
                        </div>
                    </div>

                    <footer className="form-footer">
                        <button type="submit" className="submit-btn" disabled={submitting}>
                            <FontAwesomeIcon icon={faSave} spin={submitting} />
                            <span>{submitting ? 'جاري الربط...' : 'إضافة الميزة للخطة'}</span>
                        </button>
                    </footer>
                </form>
            </div>

            <style>{`
                .assign-feature-page { padding: 40px 20px; background: #f8fafc; min-height: 100vh; direction: rtl;   font-family: "Zain", sans-serif; }
                .form-container { max-width: 800px; margin: 0 auto; background: white; padding: 40px; border-radius: 24px; box-shadow: 0 10px 40px rgba(0,0,0,0.05); }
                .form-header { display: flex; align-items: center; gap: 20px; margin-bottom: 40px; border-bottom: 2px solid #f1f5f9; padding-bottom: 20px; }
                .back-btn { background: #f1f5f9; border: none; padding: 10px 20px; border-radius: 12px; cursor: pointer; color: #64748b; font-weight: 700; display: flex; align-items: center; gap: 10px; }
                .form-header h1 { margin: 0; font-size: 1.8rem; color: #1e293b; font-weight: 800; }
                
                .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; }
                .full-width { grid-column: 1 / -1; }
                
                .input-group label { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; font-weight: 700; color: #475569; font-size: 0.95rem; }
                .input-group input { width: 100%; padding: 14px 18px; border: 2px solid #e2e8f0; border-radius: 12px; font-size: 1rem;   font-family: "Zain", sans-serif; }
                
                .checkbox-toggle { display: flex; justify-content: space-between; align-items: center; padding: 15px 20px; background: #f8fafc; border: 2px solid #e2e8f0; border-radius: 12px; cursor: pointer; }
                .checkbox-toggle .label-info { display: flex; align-items: center; gap: 12px; font-weight: 700; color: #334155; }
                .checkbox-toggle input { width: 22px; height: 22px; accent-color: #6366f1; }

                .field-error { color: #ef4444; font-size: 0.85rem; margin-top: 8px; font-weight: 700; }
                .input-error { border-color: #ef4444 !important; }

                .form-footer { margin-top: 40px; padding-top: 30px; border-top: 2px solid #f1f5f9; display: flex; justify-content: center; }
                .submit-btn { background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%); color: white; border: none; padding: 16px 50px; border-radius: 15px; font-size: 1.1rem; font-weight: 700; cursor: pointer; display: flex; align-items: center; gap: 12px; transition: all 0.3s; box-shadow: 0 10px 20px -5px rgba(99, 102, 241, 0.4); }
                
                @media (max-width: 768px) {
                    .form-grid { grid-template-columns: 1fr; }
                    .form-container { padding: 25px; }
                }
            `}</style>
        </div>
    );
};

export default AddPlanFeature;
